import React, {useMemo, useState, useEffect, useContext} from 'react';
import PropTypes from "prop-types";
import { PieChart } from './PieChart';
import getDataByCountryCode from "../../common/impact/data/CountryData";
import { getStaticPlanDataByPricingLevel } from "../../subscribe-guest/plansData";
import Legend from "./Legend";
import * as link from "../../common/pages";
import * as answersActions from "../../../redux/actions/personalFootprintActions";
import { useHistory } from "react-router-dom";
import { Modal, notification } from "antd";
import "./Results.css";
import {householdCalculation} from '../Calculations.js';
import { postIframeData } from "../../../util/iframePostMessageUtil";
import { convertToImperialTon } from "../../../util/unitConverter";
import { connect } from 'react-redux';
import { isEqual } from "lodash";
import { bindActionCreators } from 'redux';
import climateAwareSmallBadge from '../../../images/footprint/climate-aware-small.svg'
import generatePathWithCurrentMerchantCodeAsBase from "../../../util/generatePathWithCurrentMerchantCodeAsBase";
import UIContext from "../../../util/UIContext";

function Results({ answers, footprintHousehold, footprintOfOneAdult, numberOfAdults, numberOfChildren, countryCode, hideRecommendedPanel, merchantConfig, currentUserCognito, actions, isHideSave, personalFootprintData }) {
  const history = useHistory();

  const [modalOpen, setModalOpen] = useState(false);
  const [congratulationsModalOpen, setCongratulationsModalOpen] = useState(false);
  const [lastSavedFootprint, setLastSavedFootprint] = useState(null);
  const disableSaving = useMemo(() => isEqual(lastSavedFootprint, footprintHousehold), [lastSavedFootprint, footprintHousehold]);

  const countryData = getDataByCountryCode(countryCode);
  const countryAvgFootprint = countryData.footprint;
  const uiContext = useContext(UIContext);
  
  const globalAvgFootprint = 9.8; //unknown data source

  const getWeightUnitForLocale = () => countryData.unitSystem === "IMPERIAL" ? "tons" : "tonnes";
  const getWeightAmountForLocale = (value) => countryData.unitSystem === "IMPERIAL" ? convertToImperialTon(value, 't') : value;


  useEffect( () =>
    {
      if(footprintHousehold && merchantConfig?.offsetAction === "IFRAME_POST_MESSAGE"){
        sendResultsIFramePostMessage('POST_PERSONAL_CALCULATOR_RESULT_INTERIM');
      }
    },
  [footprintHousehold]);

  const subscribePageUrl = generatePathWithCurrentMerchantCodeAsBase(link.SUBSCRIBE_GUEST);

  const offsetButtonClicked = () => {
    if (merchantConfig?.offsetAction === "IFRAME_POST_MESSAGE") {
      sendResultsIFramePostMessage('POST_PERSONAL_CALCULATOR_RESULT');
    } else {
      history.push(`${subscribePageUrl}?recommendedPricingLevel=${suggestedPlanData && suggestedPlanData.pricingLevel}`);
    }
  }

  const sendResultsIFramePostMessage = (eventType) => {
      const footprintInKgs = Object.entries(footprintHousehold).reduce((newObj, [key, val]) => {
        newObj[`${key.toLowerCase()}Kg`] = val * 1000;
        return newObj;
      }, {});
      postIframeData({
        type: eventType,
        payload: {
          calculationSchemaVersion: 1,
          allEmissionsKg: totalFootprintOneAdult * 1000,
          emissions: {
            ...footprintInKgs
          }
        }
      });
  }

  const OpenResultsModal = () => {
    setModalOpen(true)
  }

  const totalFootprintOneAdult = !footprintOfOneAdult ? 0 : (footprintOfOneAdult.ENERGY + footprintOfOneAdult.DIET + footprintOfOneAdult.WASTE + footprintOfOneAdult.DRIVING + footprintOfOneAdult.FLIGHTS + footprintOfOneAdult.UNAVOIDABLE);

  const localisedCategorisedFootprintOneAdult = footprintOfOneAdult && {
    Transport:getWeightAmountForLocale(footprintOfOneAdult.DRIVING + footprintOfOneAdult.FLIGHTS),
    Energy:getWeightAmountForLocale(footprintOfOneAdult.ENERGY),
    Food:getWeightAmountForLocale(footprintOfOneAdult.DIET),
    CountryEmissions:getWeightAmountForLocale(footprintOfOneAdult.UNAVOIDABLE) ,
    Other:getWeightAmountForLocale(footprintOfOneAdult.WASTE)
  };

  const colors = ['#015A76', '#4DA7B3', '#008A00', '#BFEDBF', '#CCCCCC'];

  const countryPercentage = ((totalFootprintOneAdult / countryAvgFootprint) * 100);
  const globalPercentage = ((totalFootprintOneAdult / globalAvgFootprint) * 100);

  const percentageForSuggestion = ((householdCalculation({
      baseAmount: totalFootprintOneAdult,
      numAdults: numberOfAdults,
      numChildren: numberOfChildren
  }) / countryData.footprint) * 100);

  let suggestedPlanTier = Math.round(percentageForSuggestion / 50) * 10; // fudge factor to get a good spread of plans suggested
  suggestedPlanTier = suggestedPlanTier < 10 ? 10 : (suggestedPlanTier > 40 ? 40 : suggestedPlanTier);
  const suggestedPlanData = getStaticPlanDataByPricingLevel(Math.min(40, suggestedPlanTier));

  const saveFootprint = () => {
    setLastSavedFootprint(footprintHousehold);
    const params = new URLSearchParams(window.location.search)
    params.delete("countryChangedByUser")
    history.push({search: params.toString()})
    const isFirstTime = !personalFootprintData.answers;
    actions.saveFootprintAndAnswers(currentUserCognito?.authData?.email, footprintHousehold, answers, footprintOfOneAdult)
      .then(() => {
        if (isFirstTime) {
          setCongratulationsModalOpen(true)
        } else {
          notification.success({
            message: 'Footprint Saved',
            description: 'Your footprint will be used on your dashboard',
            placement: 'topRight'
          });
        }
      })
      .catch((respoonse) => {
        setLastSavedFootprint(null);
        console.err(respoonse);
        notification.error({
          message: 'Error',
          description: 'Error saving footprint, see console for details',
          placement: 'topRight'
        });
      })
  };

  const getFootPrintInfo = (percentage) => {
    const fixedPercentage = percentage.toFixed(0);
    let footprintInfo = {
      value: "",
      text: ""
    }
    const display = 100 - fixedPercentage;
    if ((display) > 0) {
      footprintInfo.text = "less than"
      footprintInfo.value = `${(display)}%`
    } else if ((display) < 0) {
      footprintInfo.text = "more than"
      footprintInfo.value = `${(Math.abs(display))}%`
    }
    return footprintInfo;
  }


  let countryFootprintInfo = getFootPrintInfo(countryPercentage);
  let globalFootprintInfo = getFootPrintInfo(globalPercentage);

  let oneAdultFootprintForLocale = getWeightAmountForLocale(totalFootprintOneAdult).toFixed(1);
  return (
    <>
      <div className='section-heading'>{'YOUR RESULT'}</div>

      {footprintHousehold &&
        <>
          <div style={{ paddingBottom: 15 }}>
            <div><span className='footprint-total'>{oneAdultFootprintForLocale}</span> <span style={{ fontSize: 16, fontWeight: "bold" }}> {getWeightUnitForLocale()} CO2 per year</span></div>
            <p><span className="font-bold color-primary cursor-pointer" onClick={OpenResultsModal}>How is this calculated?</span></p>
          </div>
          <div className="mb-3">
            {totalFootprintOneAdult && <PieChart categorisedFootprint={localisedCategorisedFootprintOneAdult} colors={colors} />}
            {totalFootprintOneAdult && <Legend categorisedFootprint={localisedCategorisedFootprintOneAdult} colors={colors} />}
          </div>
            <>
            {currentUserCognito?.isSignedIn && !isHideSave && <div className="banner-1 banner-3 d-block border-r4 mb-3">
              <div className="mb-2">
                {disableSaving ?
                    <span>{`You've set your footprint to ${oneAdultFootprintForLocale} ${getWeightUnitForLocale()}`}</span> :
                    <span>Save this footprint to my dashboard</span>}
              </div>
              <button className="btn btn-light" disabled={disableSaving} onClick={saveFootprint}>
                {disableSaving ? "Footprint Saved" : "Save Footprint"}
              </button>
            </div>}
              <div>
              <p>{`Your approximate personal carbon footprint is `}<b>{oneAdultFootprintForLocale}</b>{` ${getWeightUnitForLocale()} CO2 per year, which is `}<b>{`${countryFootprintInfo.value} ${countryFootprintInfo.text}`}</b>
            {` the average footprint in ${countryData.name} and `}<b>{`${globalFootprintInfo.value} ${globalFootprintInfo.text}`}</b>{` the world average.`}
              </p>
              </div>
              <div>
                {uiContext.content.personalCalculator.items.includes('learnToReduce') &&
                  <p>
                    {`Learn how to reduce your footprint with our `}<a
                    target="_blank" rel="noopener noreferrer"
                    href={`https://carbonclick.com/what-is-a-carbon-footprint`}>reduce guide</a> {` and consider offsetting the rest.`}
                  </p>
                }
                <p>{`Offsetting is a powerful tool that reduces your carbon footprint with high quality carbon offset projects.`}</p>
              </div>

            {hideRecommendedPanel === false && !isHideSave && <div className="banner-1 banner-2 d-block border-r4">
            {merchantConfig?.offsetAction !== "IFRAME_POST_MESSAGE" &&
              <div className="mb-2">
              <span>{<>Take climate action and offset your footprint with our <b>{suggestedPlanData && suggestedPlanData.name}</b> plan</>}</span>
              </div>
            }
              <div>
              <button className="btn btn-light" onClick={offsetButtonClicked} >{merchantConfig?.offsetButtonText}</button>
              </div>
              </div>}

              <Modal visible={modalOpen} onCancel={() => setModalOpen(false)} onOk={() => setModalOpen(false)} className="group-editor-modal">
              <h3>We estimate your carbon footprint is {oneAdultFootprintForLocale} {getWeightUnitForLocale()}</h3>
              <p>
              A carbon footprint is the total of the carbon emissions from a range of lifestyle factors, including the food you eat, the home you live in and how much you travel.
              <br/><br/>
              To help us calculate this we have used data from many different sources including, <a rel="noopener noreferrer" href="https://ourworldindata.org/environmental-impacts-of-food#the-carbon-footprint-of-eu-diets-where-do-emissions-come-from" target="_blank">OurWorldInData.org</a>, <a rel="noopener noreferrer" href="https://www.carbonfootprint.com/docs/2020_07_emissions_factors_sources_for_2020_electricity_v1_3.pdf" target="_blank">CarbonFootprint.com</a>, <a rel="noopener noreferrer" href={`https://ec.europa.eu/eurostat/databrowser/view/env_air_gge/default/table?lang=en`} target="_blank">EuroStats</a> and many others to build a comprehensive picture of how our lifestyles impact on the planet.
              <br/><br/>
              To make the calculator as simple and yet powerful as possible we looked at which lifestyle factors contribute most to an individual’s footprint. We found Diet, Travel, Energy, Waste and Flights taken to be the most impactful factors.
              <br/><br/>
              The per capita footprints for each country provided by <a rel="noopener noreferrer" target="_blank" href={`https://edgar.jrc.ec.europa.eu/overview.php?v=booklet2019&dst=GHGpc`}>Eurostats</a> are calculated from the country&apos;s total footprint which includes emissions from sources such as public services, infrastructure and exports. These &apos;Country emissions&apos; are included in your results, and may make your footprint appear higher than you would expect from your own activities. However, it is imperative to include these to get a true measure of a citizen&apos;s footprint.
              </p>
              </Modal>
              <Modal
                centeted
                visible={congratulationsModalOpen}
                footer={false}
                title={false}
                onCancel={() => setCongratulationsModalOpen(false)}
              >
                <div className="text-center">
                  <h4><b>You’ve unlocked the<br/>Climate Aware badge!</b></h4>
                  <img height={100} className="center my-4" src={climateAwareSmallBadge} alt=""/>
                  Thank you for learning about, and saving your carbon footprint.<br/>You’ve earned the Climate Aware Badge!
                </div>
              </Modal>
          </>
        </>
      }
    </>
  );

}

const mapStateToProps = (state) => ({
  currentUserCognito: state.currentUserCognito,
  personalFootprintData: state.personalFootprintData
});

const mapDispatchToProps = dispatch => ({
  actions: {
    saveFootprintAndAnswers: bindActionCreators(
      answersActions.savePersonalFootprint,
      dispatch
    )
  }
});

Results.propTypes = {
  footprintHousehold: PropTypes.object,
  numberOfAdults: PropTypes.number,
  numberOfChildren: PropTypes.number,
  answers: PropTypes.object
};

export default connect(mapStateToProps, mapDispatchToProps)(Results);
